import { IconButton, TextField, Typography } from '@mui/material';
import styled, { keyframes } from 'styled-components';

const typing = keyframes`
    0%, 60%, 100% {
        transform: translateY(0);
        opacity: 0.7;
    }
    30% {
        transform: translateY(-10px);
        opacity: 1;
    }
`;

const pulse = keyframes`
    0%, 100% {
        opacity: 1;
    }
    50% {
        opacity: 0.5;
    }
`;

export const ChatTrigger = styled(IconButton)`
    &&.MuiIconButton-root {
        background-color: #b31d15;
        color: white;
        width: 50px;
        height: 50px;
        box-shadow: 0 4px 12px rgba(179, 29, 21, 0.3);
        border-radius: 50%;
        overflow: visible;
        img {
            ${(props) => (props.isADIFullBody ? 'scale: 1;' : 'scale: 1.15;')}
        }
        &:hover img {
            transform: scale(1.1);
        }
    }
`;

export const ChatTriggerAlt = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    cursor: pointer;

    /* estilos base provenientes de customStyles */
    gap: ${({ customStyles }) => customStyles?.gap ?? '0.5rem'};
    padding: ${({ customStyles }) => customStyles?.padding ?? '0.5rem 1rem'};
    border-radius: ${({ customStyles }) =>
        customStyles?.borderRadius ?? '180px'};
    border: ${({ customStyles }) => customStyles?.border ?? 'none'};
    width: ${({ customStyles }) => customStyles?.width ?? 'auto'};
    font-size: ${({ customStyles }) => customStyles?.fontSize ?? 'inherit'};
    background-color: ${({ customStyles }) =>
        customStyles?.backgroundColor ?? 'transparent'};
    color: ${({ customStyles }) => customStyles?.color ?? 'inherit'};

    svg {
        font-size: 24px;
        color: inherit;
    }

    &:hover {
        /* Soporta varias formas de definir hover en customStyles */
        background-color: ${({ customStyles }) =>
            customStyles?.['&:hover']?.backgroundColor ??
            customStyles?.['&hover']?.backgroundColor ??
            customStyles?.hoverBackgroundColor ??
            '#b31d15'};
        color: ${({ customStyles }) =>
            customStyles?.['&:hover']?.color ??
            customStyles?.['&hover']?.color ??
            customStyles?.hoverColor ??
            '#fff'};

        svg,
        i,
        * {
            color: ${({ customStyles }) =>
                customStyles?.['&:hover']?.color ??
                customStyles?.['&hover']?.color ??
                customStyles?.hoverColor ??
                '#fff'};
        }
    }
`;

export const Overlay = styled.div`
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.35);
    z-index: 999;
`;

export const ChatContainer = styled.div`
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90vw;
    height: 90vh;
    background: white;
    border-radius: 20px;
    box-shadow: 0 24px 48px rgba(0, 0, 0, 0.18);
    z-index: 1000;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    transition: height 0.25s ease-in-out;
    @media (max-width: 1024px) {
        width: 90vw;
        height: '60vh';
    }
    @media (max-width: 768px) {
        width: calc(100vw - 2rem);
        height: '75vh'};
        left: 50%;
        transform: translate(-50%, -50%);
        bottom: auto;
    }
`;

export const ChatHeader = styled.div`
    background: linear-gradient(270.23deg, #b31d15 0.14%, #e01f14 99.79%);
    padding: 16px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: white;
    min-height: 80px;
    box-sizing: border-box;
`;

export const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

export const HeaderInfo = styled.div`
    display: flex;
    flex-direction: column;
`;

export const HeaderActions = styled.div`
    display: flex;
    gap: 4px;
`;

export const MessagesContainer = styled.div`
    flex: 1;
    padding: 16px;
    padding-bottom: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: white;
    position: relative;
    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-track {
        background: #f1f1f1;
    }

    &::-webkit-scrollbar-thumb {
        background: #cccccc;
        border-radius: 2px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: #b0b0b0;
    }
`;

export const MessageBubble = styled.div`
    display: flex;
    flex-direction: column;
    align-items: ${(props) => (props.isUser ? 'flex-end' : 'flex-start')};
    margin-bottom: 8px;
`;

export const MessageText = styled.div`
    background: ${(props) => (props.isUser ? '#b31d15' : '#e9ecef')};
    color: ${(props) => (props.isUser ? 'white' : '#333')};
    padding: 10px 14px;
    border-radius: 18px;
    border-bottom-right-radius: ${(props) => (props.isUser ? '4px' : '18px')};
    border-bottom-left-radius: ${(props) => (props.isUser ? '18px' : '4px')};
    max-width: 80%;
    word-wrap: break-word;
    font-size: 14px;
    line-height: 1.4;
    white-space: pre-line;
    strong {
        font-size: 14px;
        font-weight: 600;
    }
`;

export const MessageTime = styled.div`
    font-size: 11px;
    color: #666;
    margin-top: 4px;
    padding: 0 8px;
`;

export const TypingIndicator = styled.div`
    background: #e9ecef;
    padding: 1rem 12px;
    border-radius: 18px;
    border-bottom-left-radius: 4px;
    display: flex;
    gap: 0.5rem;
    align-items: end;
`;

export const TypingDot = styled.div`
    width: 6px;
    height: 6px;
    background: #666;
    border-radius: 50%;
    animation: ${typing} 1.4s infinite ease-in-out;
    &:nth-child(1) {
        animation-delay: 0s;
    }

    &:nth-child(2) {
        animation-delay: 0.16s;
    }

    &:nth-child(3) {
        animation-delay: 0.32s;
    }
`;

export const TypingText = styled(Typography)`
    animation: ${pulse} 1.5s ease-in-out infinite;
`;

export const IndicatorWrapper = styled.div`
    display: flex;
    align-items: end;
    gap: 4px;
`;

export const PredefinedOptionsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 12px 16px;
    margin: 0;
    background: white;
    border-top: 1px solid #f1f1f1;
    z-index: 10;
`;

export const PredefinedOptionButton = styled.button`
    appearance: none;
    border: none;
    background: ${(props) => (props.isSelected ? '#b31d15' : '#fff')};
    color: ${(props) => (props.isSelected ? 'white' : '#b31d15')};
    padding: 8px 12px;
    border-radius: 18px;
    border: 1px solid #b31d15;
    cursor: pointer;
    font-size: 13px;
    line-height: 1.2;
    transition: all 0.2s ease;
    &:hover {
        color: white;
        background: #b31d15;
    }
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

export const UrlButton = styled.button`
    appearance: none;
    border: none;
    background-color: #b31d15;
    color: #fff;
    padding: 8px 12px;
    border-radius: 18px;
    cursor: pointer;
    font-size: 13px;
    line-height: 1.2;
    margin-top: 8px;
    align-self: flex-start;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
`;

export const FooterContainer = styled.div`
    display: flex;
    gap: 8px;
    align-items: flex-end;
    width: 100%;
    box-sizing: border-box;
`;

export const InputContainer = styled.div`
    padding: 4px 16px 16px 16px;
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: center;
    width: 100%;
    border-radius: 25px;
`;

export const MessageInput = styled(TextField)`
    && {
        .MuiOutlinedInput-root {
            border-radius: 20px;
            background: #f8f9fa;
            font-size: 16px;
            fieldset {
                border-color: #e9ecef;
            }

            &:hover fieldset {
                border-color: #b31d15;
            }

            &.Mui-focused fieldset {
                border-color: #b31d15;
            }
        }
    }
`;

export const SendButton = styled(IconButton)`
    &&.MuiIconButton-root {
        background-color: #b31d15;
        color: white;
        width: 40px;
        height: 40px;

        &:hover {
            background-color: #941710;
        }

        &:disabled {
            background-color: #cccccc;
            color: #666;
        }
    }
`;

export const Image = styled.img`
    width: 50px;
    height: 50px;
    border-radius: 50%;
    transition: transform 0.2s ease-in-out;
`;

export const AvatarImage = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background-color: #b31d15;
    img {
        scale: 1.4;
    }
`;
